import { useEffect, useState, useRef, useMemo } from 'react';
import useServerTable from '../utils/useServerTable';
import useColumnPreferences, { useHiddenFilterReset } from '../utils/useColumnPreferences';
import * as XLSX from 'xlsx';
import Drawer from './Drawer';
import VendorForm from './VendorForm';
import PageHeader from './ui/PageHeader';
import TableState from './ui/TableState';
import ColumnPicker from './ui/ColumnPicker';
import { ColumnCells, ColumnFilterRow, ColumnHeaderRow, StatusBadge } from './ui/TableBits';
import { formatDate } from '../utils/dateUtils';
import { useToast } from './ui/Toast';

// One key per filterable column; each maps to a same-named query param on
// GET /vendors (backend supplier Vendor CRUD).
const EMPTY_FILTER = { name: '', status: '', invoiced: '', accessCode: '' };

const STATUS_OPTIONS = ['Active', 'Inactive'];

function VendorTable({ onEdit }) {
  const toast = useToast();
  const {
    rows: vendors, loading, error,
    filter, setFilter, handleFilterChange, clearFilters, hasActiveFilters,
    sortConfig, handleSort,
    page, setPage, size, handleChangePageSize,
    totalElements, totalPages, reload, fetchAll,
  } = useServerTable('/vendors', { emptyFilter: EMPTY_FILTER, initialSort: { key: 'name', direction: 'asc' } });

  // Drawer state: null when closed, { mode: 'create' } or { mode: 'edit', id } when open
  const [drawer, setDrawer] = useState(null);
  const formRef = useRef(null);
  const openCreate = () => setDrawer({ mode: 'create' });
  // onEdit lets a parent take over editing; otherwise the internal drawer opens.
  const openEdit = (vendor) => { if (onEdit) onEdit(vendor); else setDrawer({ mode: 'edit', id: vendor.id }); };
  const closeDrawer = () => setDrawer(null);
  const handleDrawerSuccess = () => { closeDrawer(); reload(); };

  const handleDownloadExcel = async () => {
    try {
      const rows = await fetchAll();
      const ws = XLSX.utils.json_to_sheet(rows.map(v => ({
        Name: v.name, Status: v.status,
        'Start Date': v.startDate, 'End Date': v.endDate,
        City: v.city, State: v.state,
        Invoiced: v.invoiced, 'Access Code': v.accessCode,
      })));
      const wb = XLSX.utils.book_new(); XLSX.utils.book_append_sheet(wb, ws, 'Vendors');
      XLSX.writeFile(wb, 'vendors.xlsx');
    } catch (e) {
      toast.error(e.message || 'Export failed');
    }
  };

  // Column catalogue in default order. Name is locked because it is the row's
  // open link and the record's identity.
  const columnCatalogue = useMemo(() => [
    {
      key: 'name', label: 'Name', locked: true,
      filter: { name: 'name' },
      cell: v => (
        <td>
          <button type="button" className="btn-link table-link" onClick={() => openEdit(v)} title="Open vendor">
            {v.name}
          </button>
        </td>
      ),
    },
    {
      key: 'status', label: 'Status',
      filter: { name: 'status', options: STATUS_OPTIONS },
      cell: v => <td><StatusBadge status={v.status} /></td>,
    },
    {
      key: 'startDate', label: 'Start Date',
      cell: v => <td style={{ color: 'var(--muted)', fontSize: '13px' }}>{formatDate(v.startDate)}</td>,
    },
    {
      key: 'endDate', label: 'End Date',
      cell: v => <td style={{ color: 'var(--muted)', fontSize: '13px' }}>{formatDate(v.endDate)}</td>,
    },
    {
      key: 'city', label: 'City',
      cell: v => <td style={{ color: 'var(--muted)' }}>{v.city}</td>,
    },
    {
      key: 'state', label: 'State',
      cell: v => <td style={{ color: 'var(--muted)' }}>{v.state}</td>,
    },
    {
      key: 'invoiced', label: 'Invoiced',
      filter: { name: 'invoiced', options: [{ value: 'true', label: 'Invoiced only' }] },
      cell: v => (
        <td>
          {Boolean(v.invoiced)
            ? <span className="badge badge-success">Yes</span>
            : <span className="badge badge-neutral">No</span>}
        </td>
      ),
    },
    {
      key: 'accessCode', label: 'Access Code',
      filter: { name: 'accessCode' },
      cell: v => <td><code className="code-pill">{v.accessCode}</code></td>,
    },
    // openEdit closes over onEdit/setDrawer, both stable for this table's life
  ], []);

  const {
    columns, pickerColumns, toggle, move, step, reset, isDefault, saveError,
  } = useColumnPreferences('vendors', columnCatalogue);

  useEffect(() => { if (saveError) toast.error(saveError); }, [saveError, toast]);
  useHiddenFilterReset(columns, setFilter, EMPTY_FILTER);

  return (
    <div className="page-container">
      <PageHeader
        title="Vendors"
        subtitle="Manage suppliers"
        actions={
          <>
            <button className="btn-info" onClick={handleDownloadExcel}>
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" style={{ marginRight: 6 }}><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
              Export
            </button>
            <button className="btn-primary" onClick={openCreate}>
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" style={{ marginRight: 6 }}><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
              New Vendor
            </button>
          </>
        }
      />

      <div className="filter-bar">
        <button className="btn-secondary" onClick={clearFilters}>Clear filters</button>
        <ColumnPicker
          columns={pickerColumns}
          onToggle={toggle}
          onMove={move}
          onStep={step}
          onReset={reset}
          isDefault={isDefault}
        />
      </div>

      <div className="table-wrapper">
        <table className="data-table" aria-busy={loading}>
          <thead>
            <ColumnHeaderRow columns={columns} sortConfig={sortConfig} onSort={handleSort} />
            <ColumnFilterRow columns={columns} filter={filter} onChange={handleFilterChange} />
          </thead>
          <tbody style={{ opacity: loading && vendors.length > 0 ? 0.55 : 1 }}>
            {vendors.length === 0 ? (
              <TableState
                colSpan={columns.length}
                loading={loading}
                error={error}
                onRetry={reload}
                filtered={hasActiveFilters}
                onClearFilters={clearFilters}
                emptyTitle="No vendors yet"
                emptyHint="Add your first supplier to start tracking vendor records."
                emptyAction={{ label: 'New Vendor', onClick: openCreate }}
              />
            ) : vendors.map(v => (
              <tr key={v.id}>
                <ColumnCells columns={columns} row={v} />
              </tr>
            ))}
          </tbody>
        </table>
        <div className="pagination">
          <span style={{ fontSize: '13px', color: 'var(--muted)', marginRight: 'auto' }}>
            {typeof totalElements === 'number' ? `${totalElements} total` : ''}
          </span>
          <label style={{ fontSize: '13px', color: 'var(--muted)' }}>Rows:&nbsp;
            <select value={size} onChange={handleChangePageSize} style={{ fontSize: '13px', border: '1px solid var(--hairline)', borderRadius: '6px', padding: '3px 6px' }}>
              {[5, 10, 25, 50, 100].map(o => <option key={o} value={o}>{o}</option>)}
            </select>
          </label>
          <span style={{ fontSize: '13px', color: 'var(--muted)' }}>Page {totalPages > 0 ? page + 1 : 0} of {totalPages}</span>
          <button onClick={() => setPage(p => Math.max(p - 1, 0))} disabled={page === 0}>← Prev</button>
          <button onClick={() => setPage(p => Math.min(p + 1, totalPages - 1))} disabled={totalPages === 0 || page >= totalPages - 1}>Next →</button>
        </div>
      </div>

      <Drawer
        open={drawer !== null}
        onClose={closeDrawer}
        title={drawer?.mode === 'edit' ? 'Edit vendor' : 'New vendor'}
        subtitle={drawer?.mode === 'edit' ? 'Update vendor details.' : 'Add a supplier vendor.'}
        footer={
          <>
            <button type="button" className="btn-secondary" onClick={closeDrawer}>Cancel</button>
            <button type="button" className="btn-primary" onClick={() => formRef.current?.requestSubmit()}>
              {drawer?.mode === 'edit' ? 'Save changes' : 'Create vendor'}
            </button>
          </>
        }
      >
        {drawer && (
          <VendorForm
            key={drawer.id || 'new'}
            mode={drawer.mode}
            vendorId={drawer.id}
            formRef={formRef}
            hideActions
            onSuccess={handleDrawerSuccess}
          />
        )}
      </Drawer>
    </div>
  );
}

export default VendorTable;
